import React from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {RoundButton} from '@optimaxdev/design-system';
import {Approve, Copy, Icon} from '@optimaxdev/icons';

import {CART_PROMOTION_BANNER} from 'constants/analytics/events/cart';
import {useIsCartPage} from 'desktop/hooks/useIsCartPage/useIsCartPage';

import {useCopyWithTimeout} from 'hooks/useCopyWithTimeout/useCopyWithTimeout';

import * as BogoIcon from '../../img/bogo.svg';
import * as DollarsDiscountIcon from '../../img/dollars-discount.svg';
import * as FreeGlasses from '../../img/freeglasses.svg';
import * as PercentageDiscountIcon from '../../img/percentage-discount.svg';

import {CouponType} from '../../types';

import s from './coupon.scss';

export type PropsType = {
    coupon: CouponType;
};

const getDiscountValue = (
    type: CouponType['discount_type'],
    value: CouponType['discount_value'],
) => {
    if (type === 'bogo')
        return (
            <span className={s.bogoIcon}>
                <Icon icon={BogoIcon} />
            </span>
        );
    if (type === 'freeglasses')
        return (
            <span className={s.freeGlassesIcon}>
                <Icon icon={FreeGlasses} />
            </span>
        );
    return (
        <span className={s.discountValue}>
            {value}
            {type === 'dollars' ? (
                <span className={s.dollarsDiscount}>
                    <Icon icon={DollarsDiscountIcon} />
                </span>
            ) : (
                <span className={s.percentageDiscount}>
                    <Icon icon={PercentageDiscountIcon} />
                </span>
            )}
        </span>
    );
};

/**
 * A coupon list item
 */
export const Coupon: React.FC<PropsType> = ({
    coupon: {
        code,
        details_description: detailsDescription,
        details_title: detailsTitle,
        discount_type: discountType,
        discount_value: discountValue,
        title,
    },
}) => {
    const {copyText, isCopied} = useCopyWithTimeout();
    const isCartPage = useIsCartPage();

    const handleButtonClick = () => {
        copyText(code);

        if (isCartPage)
            sendDesktopAnalyticsEvent(CART_PROMOTION_BANNER, {
                action: 'Copy Code',
                label: code,
            });
    };

    return (
        <li className={s.coupon} key={title}>
            <div className={s.discount}>
                {getDiscountValue(discountType.toLowerCase(), discountValue)}
            </div>
            <div className={s.details}>
                <h3 className={s.detailsTitle}>{detailsTitle}</h3>
                <p className={s.detailsDescription}>{detailsDescription}</p>
            </div>
            <div className={s.divider} />
            <div className={s.couponCode}>
                <p className={s.codeTitle}>Coupon code:</p>
                <p className={s.code}>{code}</p>
                <RoundButton
                    startIcon={
                        isCopied ? (
                            <span className={s.checkIcon}>
                                <Icon height={17} icon={Approve} strokeWidth={2} width={17} />
                            </span>
                        ) : (
                            <span className={s.copyIcon}>
                                <Icon height={17} icon={Copy} strokeWidth={2} width={17} />
                            </span>
                        )
                    }
                    {...(isCopied ? {} : {onClick: handleButtonClick})}
                >
                    {isCopied ? 'Copied!' : 'Copy Code'}
                </RoundButton>
            </div>
        </li>
    );
};
